import { X } from "lucide-react";

export default function TermsModal({ isOpen, onClose, battery }) {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 overflow-y-auto">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">

                {/* HEADER */}
                <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-center">
                    <h2 className="text-2xl font-bold text-gray-900">Terms of Rental Agreement</h2>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* BODY */}
                <div className="p-6 text-sm text-gray-700 leading-relaxed space-y-6">

                    {/* Company Header */}
                    <div className="text-center bg-gray-50 p-4 rounded">
                        <p className="font-bold text-lg">SONIC E-BIKE ABN 52 342 103 170</p>
                        <p>866 Bourke Street Waterloo NSW 2017</p>
                        <p>Phone: 0420 993 083 / 0417 232 258</p>
                    </div>

                    {/* Must Not Section */}
                    <div className="bg-yellow-50 p-4 rounded border-l-4 border-yellow-500">
                        <h3 className="font-bold text-lg mb-3 text-[#FF8C42]">The customer must not:</h3>
                        <ol className="list-decimal list-outside ml-6 space-y-2">
                            <li>Sublet or hire the bike to any other person.</li>
                            <li>Allow the bike to be operated by any other person.</li>
                            <li>Operate the bike or allow it to be operated in breach of any Act, regulations, rules or bylaws relating to road traffic.</li>
                            <li>Tamper with or attempt to disable GPS tracking. <strong>A $200 penalty applies.</strong></li>
                            <li>Operate the bike or allow it to be operated outside a <strong>100 km radius</strong> Sydney CBD.</li>
                            <li><strong>Leave the bike batteries on the bike</strong> when the bike is not in use.</li>
                            <li>Modify, repair, rewire, alter or open any electrical or mechanical components without written approval.</li>
                            <li>Overload the bike or attach excessive cargo.</li>
                            <li>Use the bike off-road, in flood water, on stairs, or recklessly.</li>
                            <li>Use the bike in heavy storm conditions or allow water damage.</li>
                            <li>Remove or replace any parts without written consent.</li>
                        </ol>
                    </div>

                    {/* Must Section */}
                    <div className="bg-yellow-50 p-4 rounded border-l-4 border-yellow-500">
                        <h3 className="font-bold text-lg mb-3 text-[#FF8C42]">The customer must:</h3>
                        <ol className="list-decimal list-outside ml-6 space-y-2" start="12">
                            <li>Pay all rental fees on time.</li>
                            <li>Operate the bike responsibly and safely.</li>
                            <li>Park and store the bike safely.</li>
                            <li>Keep the bike reasonably clean.</li>
                            <li>Not remove or replace any item without consent.</li>
                            <li>Remove the battery when not in use.</li>
                            <li>Report mechanical issues immediately.</li>
                            <li>Confirm they have the skills to operate the bike.</li>
                            <li>File a police report for accidents/theft.</li>
                            <li>Return the bike on the agreed date.</li>
                        </ol>
                    </div>

                    {/* Payment Section */}
                    <div className="bg-yellow-50 p-4 rounded border-l-4 border-yellow-500">
                        <h3 className="font-bold text-lg mb-3 text-[#FF8C42]">Payment:</h3>
                        <ol className="list-decimal list-outside ml-6 space-y-2" start="22">
                            <li>Weekly rental payments must be made in advance.</li>
                            <li>Payments can be made via:
                                <ul className="list-disc ml-6 mt-2 space-y-1">
                                    <li><strong>Account Name:</strong> YK ERMIS M GUNER</li>
                                    <li>PayID using ABN 52 342 103 170</li>
                                </ul>
                            </li>
                            <li>Late payments incur a $10/day fee.</li>
                            <li>A refundable $200 deposit is required.</li>
                            <li>Early termination incurs a $200 penalty.</li>
                            <li>The hirer may charge unpaid fees to the customer’s payment method.</li>
                        </ol>
                    </div>

                    {/* Rent to Own Section */}
                    <div className="bg-yellow-50 p-4 rounded border-l-4 border-yellow-500">
                        <h4 className="font-bold text-lg mb-3 text-[#FF8C42]">Rent To Own Conditions:</h4>
                        <ol className="list-decimal list-outside ml-6 space-y-2" start="28">
                            <li>Upon completion of all payments, ownership transfers to the customer.</li>
                            <li>No refunds for early return.</li>
                        </ol>
                    </div>

                    {/* Early Return Section */}
                    <div className="bg-yellow-50 p-4 rounded border-l-4 border-yellow-500">
                        <h4 className="font-bold text-lg mb-3 text-[#FF8C42]">Early Return and Minimum Charge:</h4>
                        <ol className="list-decimal list-outside ml-6 space-y-2" start="30">
                            <li>The renter acknowledges fixed minimum rental periods.</li>
                            <li>
                                If the customer returns the bike early:
                                <ul className="list-disc ml-6 mt-2 space-y-1">
                                    <li><strong>Minimum Charge</strong>
                                        <ul className="list-disc ml-6 mt-2 space-y-1">
                                            <li>The minimum charge is based on the shortest package.</li>
                                        </ul>
                                    </li>
                                    <li><strong>Recalculation of Fees</strong>
                                        <ul className="list-disc ml-6 mt-2 space-y-1">
                                            <li>The fee is recalculated based on:
                                                <ul className="list-disc ml-6 mt-2 space-y-1">
                                                    <li>The actual number of weeks used</li>
                                                    <li>The highest applicable weekly rate</li>
                                                </ul>
                                            </li>
                                        </ul>
                                    </li>
                                    <li><strong>Early Return</strong>
                                        <ul className="list-disc ml-6 mt-2 space-y-1">
                                            <li>No refunds for unused time.</li>
                                        </ul>
                                    </li>
                                    <li><strong>Deposit</strong>
                                        <ul className="list-disc ml-6 mt-2 space-y-1">
                                            <li>The deposit is refunded after inspection.</li>
                                        </ul>
                                    </li>
                                </ul>
                            </li>
                        </ol>
                    </div>

                    {/* Loss/Damage Section */}
                    <div className="bg-red-50 p-4 rounded border-l-4 border-red-500">
                        <h4 className="font-bold text-lg mb-3 text-red-700">Loss, Stolen or Damage to Bike:</h4>
                        <ol className="list-decimal list-outside ml-6 space-y-2" start="32">
                            <li>The customer is liable for all damage.</li>
                            <li>
                                If the bike is lost or damaged beyond repair:
                                <ul className="list-disc ml-6 mt-2 space-y-1 text-xs">
                                    <li>Frame: $750</li>
                                    <li>Battery: $1200</li>
                                    <li>Display: $120</li>
                                    <li>Seat lock/keys: $15</li>
                                    <li>Remote: $40</li>
                                    <li>Helmet: $20</li>
                                    <li>Heavy duty lock: $30</li>
                                    <li>Alarm disc lock: $30</li>
                                    <li>Secure lock: $15</li>
                                    <li>Charger: $80</li>
                                    <li>Phone holder: $30</li>
                                    <li>Front rim: $100</li>
                                    <li>Rear rim: $180</li>
                                </ul>
                            </li>
                            <li>Repairable damage is charged at full cost.</li>
                            <li>The hirer may recover legal and collection fees.</li>
                            <li>The customer must remove the bike from accident scenes.</li>
                        </ol>
                    </div>

                    {/* Liability Section */}
                    <div className="bg-yellow-50 p-4 rounded border-l-4 border-yellow-500">
                        <h3 className="font-bold text-lg mb-3">Personal Injury, Personal Property and Storage of Bike:</h3>
                        <ol className="list-decimal list-outside ml-6 space-y-2" start="37">
                            <li>
                                The hirer does not accept liability for:
                                <ul className="list-disc ml-6 mt-2 space-y-1 text-xs">
                                    <li>Personal injuries</li>
                                    <li>Loss/damage to customer property</li>
                                    <li>Loss/damage to third parties</li>
                                </ul>
                            </li>
                            <li>The customer is fully responsible for lawful operation of the bike.</li>
                        </ol>
                    </div>

                    {/* Checklist Section */}
                    <div className="bg-yellow-50 p-4 rounded border-l-4 border-yellow-500">
                        <h4 className="font-bold text-lg mb-3">Checklist:</h4>
                        <ul className="list-disc ml-6 mt-2 space-y-1 text-xs">
                            <li>Helmet</li>
                            <li>Bag</li>
                            <li>Phone Holder</li>
                            <li>Seat Keys</li>
                            <li>Remote Key</li>
                            <li>Secure Lock</li>
                            <li>Heavy-Duty Bike Lock</li>
                            <li>Alarm Disc Lock</li>
                            <li>Charger</li>
                            <li>Battery: {battery}</li>
                        </ul>
                    </div>

                    {/* Customer Declaration */}
                    <div className="bg-green-50 p-4 rounded border-l-4 border-green-500">
                        <h3 className="font-bold text-lg mb-3">Customer Declaration:</h3>
                        <p>I declare that I fully understand and agree to all the terms of this Rental Agreement.</p>
                    </div>

                </div>

                {/* FOOTER */}
                <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 flex justify-end">
                    <button onClick={onClose} className="px-6 py-2 bg-[#FF8C42] text-white font-semibold rounded-lg hover:bg-[#e67a32] transition-colors">
                        Close
                    </button>
                </div>

            </div>
        </div>
    );
}
